import { ArrowLeft, Edit3, Loader2, Pause, Play } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FlowRecentRuns } from "@/components/flows/FlowRecentRuns";
import { FlowScheduler } from "@/components/flows/FlowScheduler";
import { FlowStepTree } from "@/components/flows/FlowStepTree";
import { FlowWebhookSection } from "@/components/flows/FlowWebhookSection";
import { ScheduledFlowAuthControl } from "@/components/flows/ScheduledFlowAuthControl";
import { useToast } from "@/components/shared";
import { flowService } from "@/lib/services";
import type { Flow, FlowRunSummary, FlowTrigger } from "@/types";

export function FlowDetail() {
  const { flowId } = useParams<{ flowId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [flow, setFlow] = useState<Flow | null>(null);
  const [triggers, setTriggers] = useState<FlowTrigger[]>([]);
  const [recentRuns, setRecentRuns] = useState<FlowRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showScheduler, setShowScheduler] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editingTrigger, setEditingTrigger] = useState<FlowTrigger | null>(null);

  useEffect(() => {
    if (flowId) {
      loadFlowData();
    }
  }, [flowId]);

  const loadFlowData = async () => {
    if (!flowId) return;

    try {
      setLoading(true);
      setError(null);
      const [flowRes, triggersRes, runsRes] = await Promise.all([
        flowService.get(flowId),
        flowService.getTriggers(flowId),
        flowService.getRuns(flowId, { page: 1, pageSize: 5 }),
      ]);
      setFlow(flowRes.data);
      setTriggers(triggersRes.data);
      setRecentRuns(runsRes.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load flow");
      console.error("Error loading flow:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTrigger = async (triggerUuid: string) => {
    if (!flowId) return;
    try {
      setActionLoading(triggerUuid);
      await flowService.deleteTrigger(flowId, triggerUuid);
      setTriggers((prev) => prev.filter((t) => t.uuid !== triggerUuid));
      showToast("Schedule deleted", "success");
    } catch (err) {
      console.error("Error deleting trigger:", err);
      showToast("Failed to delete schedule", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const formatSchedule = (trigger: FlowTrigger) => {
    const schedule = trigger.schedule;
    if (!schedule) return trigger.name;
    if (schedule.scheduleType === "daily") return `Daily at ${schedule.runAtTime}`;
    if (schedule.scheduleType === "weekly") {
      const days = (schedule.daysOfWeek || [])
        .map((d) => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][d])
        .join(", ");
      return `${days} at ${schedule.runAtTime}`;
    }
    return trigger.name;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error || !flow) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || "Flow not found"}</p>
          <button type="button" onClick={() => navigate("/flows")} className="text-brand-primary hover:underline">
            Back to Workflows
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border-primary px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/flows")}
              className="p-2 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-content-primary">{flow.name}</h1>
              <p className="text-content-tertiary mt-1">{flow.description || "No description"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/flows/${flowId}/edit`)}
              className="flex items-center gap-2 px-4 py-2 bg-surface-secondary text-content-primary rounded-lg hover:bg-interactive-active transition-colors"
            >
              <Edit3 className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={() => navigate(`/flows/${flowId}/run`)}
              className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              <Play className="h-4 w-4" />
              Run Flow
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-5xl space-y-6">
          {/* Paused Runs Alert */}
          {recentRuns.some((run) => run.status === "paused") && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <Pause className="h-5 w-5 text-yellow-400" />
                <div className="flex-1">
                  <h3 className="font-medium text-content-primary">
                    {recentRuns.filter((run) => run.status === "paused").length} Run(s) Pending Review
                  </h3>
                  <p className="text-sm text-content-tertiary mt-1">
                    Some flow runs are paused and waiting for human review.
                  </p>
                </div>
                <Link
                  to={`/flows/${flowId}/runs`}
                  className="px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors text-sm font-medium"
                >
                  View All Runs
                </Link>
              </div>
            </div>
          )}

          {/* Workflow Intent */}
          {flow.purpose && (
            <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
              <h2 className="text-lg font-medium text-content-primary mb-2">Workflow Intent</h2>
              <p className="text-sm text-content-secondary whitespace-pre-wrap">{flow.purpose}</p>
            </div>
          )}

          {/* Flow Steps */}
          <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
            <h2 className="text-lg font-medium text-content-primary mb-4">Flow Steps</h2>
            <FlowStepTree steps={flow.steps} connectors={flow.connectors} />
          </div>

          {/* Schedules/Triggers */}
          <div className="bg-surface-primary border border-border-primary rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-content-primary">Schedules</h2>
              <button
                onClick={() => {
                  setEditingTrigger(null);
                  setShowScheduler(true);
                }}
                className="text-sm text-brand-primary hover:underline"
              >
                + Add Schedule
              </button>
            </div>

            {showScheduler && (
              <FlowScheduler
                onCancel={() => {
                  setShowScheduler(false);
                  setEditingTrigger(null);
                }}
                onSave={async (schedule) => {
                  if (!flowId) return;
                  try {
                    if (editingTrigger) {
                      await flowService.updateTrigger(flowId, editingTrigger.uuid, {
                        schedule,
                        config: schedule.input_prompt ? { input_prompt: schedule.input_prompt } : undefined,
                      });
                    } else {
                      await flowService.createTrigger(flowId, {
                        trigger_type: "scheduled",
                        name: "Scheduled Run",
                        schedule,
                        config: schedule.input_prompt ? { input_prompt: schedule.input_prompt } : {},
                      });
                    }
                    setShowScheduler(false);
                    setEditingTrigger(null);
                    await loadFlowData();
                    showToast(editingTrigger ? "Schedule updated" : "Schedule created", "success");
                  } catch (err) {
                    console.error("Error saving schedule:", err);
                    showToast("Failed to save schedule", "error");
                  }
                }}
                initialSchedule={
                  editingTrigger?.schedule
                    ? {
                        schedule_type: editingTrigger.schedule.scheduleType,
                        timezone: editingTrigger.schedule.timezone,
                        run_at_time: editingTrigger.schedule.runAtTime,
                        days_of_week: editingTrigger.schedule.daysOfWeek,
                        interval_days: editingTrigger.schedule.intervalDays,
                        start_date: editingTrigger.schedule.startDate,
                        input_prompt: (editingTrigger.config as Record<string, string>)?.input_prompt,
                      }
                    : undefined
                }
              />
            )}

            {(() => {
              const scheduled = triggers.filter((t) => t.triggerType === "scheduled");
              if (scheduled.length === 0 && !showScheduler) {
                return <p className="text-content-tertiary text-sm">No schedules configured.</p>;
              }
              return (
                <div className="space-y-3">
                  {scheduled.map((trigger) => (
                    <div
                      key={trigger.uuid}
                      className="flex items-center justify-between p-4 bg-surface-secondary rounded-lg border border-border-secondary"
                    >
                      <div className="flex-1">
                        <div className="text-content-primary font-medium">{formatSchedule(trigger)}</div>
                        <div className="text-xs text-content-tertiary mt-0.5">
                          {trigger.schedule?.timezone || "UTC"}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingTrigger(trigger);
                            setShowScheduler(true);
                          }}
                          className="px-3 py-1.5 text-xs text-content-secondary hover:text-content-primary bg-surface-primary rounded-md border border-border-secondary hover:border-border-hover transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteTrigger(trigger.uuid)}
                          disabled={actionLoading === trigger.uuid}
                          className="px-3 py-1.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-50"
                        >
                          {actionLoading === trigger.uuid ? "..." : "Delete"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            <ScheduledFlowAuthControl
              flowId={flowId!}
              hasScheduledTrigger={triggers.some((t) => t.triggerType === "scheduled")}
            />
          </div>

          {/* Recent Runs */}
          <FlowRecentRuns flowId={flowId!} runs={recentRuns} />

          {/* Webhook */}
          <FlowWebhookSection flowId={flowId!} triggers={triggers} onTriggersChange={setTriggers} />
        </div>
      </div>
    </div>
  );
}
